import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { PersonalInfoFormData, StrengthObjType } from "../../../util/interfaces";
import {
  CaptchaContainer, CaptchaLegal,
  CheckboxCopy,
  Form,
  NameContainer,
  NameInput,
  StyledA,
  StyledField, SubmitButton
} from "../../../styles/styled";
import { Checkbox, PasswordInput, TextInput } from "shared-react";
import { debounce, getPasswordStrength } from "../../../util/helper-methods";
import PasswordStrength from "../../password-strength/password-strength";
import ReCAPTCHA from "react-google-recaptcha";
import { FormCardH2, Spacer } from "../index";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { defaultStrengthObj } from "../../../util/constants";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import BusinessWaitlist from "../businessWaitlist";
import { AnimatePresence } from "framer-motion";

interface BusinessDetailsFormProps {
  onSubmit: SubmitHandler<PersonalInfoFormData>;
  recaptchaRef: React.RefObject<ReCAPTCHA>;
  showWaitlist: boolean;
}

const schema = yup.object().shape({
  givenName: yup.string().required('First name is required.'),
  familyName: yup.string().required('Last name is required.').min(2, 'A valid last name is required.'),
  email: yup.string().email('A valid email is required').required('Email is required.'),
  password: yup.string(),
  marketingOptInStatus: yup.boolean(),
  agreedToTOSandPP: yup.boolean().test(
    'agreedToTOSandPP',
    'Please agree to the Terms of Service and Privacy Policy.',
    value => value === true
  ),
});


const PersonalInfoForm: React.FC<BusinessDetailsFormProps> = React.forwardRef(({ onSubmit, recaptchaRef, showWaitlist }) => {
  const [passwordStrength, setPasswordStrength] = useState<StrengthObjType>(defaultStrengthObj);
  const captchaDisabled = useSelector((state: RootState) => state.auth.captchaDisabled);
  const captchaKey = useSelector((state: RootState) => state.auth.captchaKey);
  const queryParams = useSelector((state: RootState) => state.app.queryParams);
  const { register, handleSubmit, watch, control, setValue, formState: { errors } } =useForm(
    {
      resolver: yupResolver(schema)
    });

  useEffect(() => {
    // determineShowWaitlist();
    // the query params are not always parsed at render time, so this logic must be added to useEffect
    const {firstName, lastName, email} = queryParams;
    // forced to do it this way because poynt passes givenName and familyName back as firstName and Last Name
    firstName && setValue('givenName', decodeURIComponent(firstName));
    lastName && setValue('familyName', decodeURIComponent(lastName));
    email && setValue('email', decodeURIComponent(email));
  }, [queryParams, setValue]);

  // using this to overwrite the change handler of password (overwritten hook form handler doesn't handle autofill)
  const passwordChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('password', event.target.value);
    determineStrength(event);
  }

  const determineStrength = (event: React.ChangeEvent<HTMLInputElement>) => {
    const temp = getPasswordStrength(event.target.value);
    setPasswordStrength(temp);
  }

  return (
    <>
      {showWaitlist ? (
        <AnimatePresence>
          <BusinessWaitlist />
        </AnimatePresence>
      ) : (
        <AnimatePresence>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormCardH2>Your Contact Information</FormCardH2>
            <NameContainer>
              <StyledField errorMessage={errors.givenName ?.message} error={errors.givenName} label="First Name" style={{ marginRight: '30px' }}>
                <NameInput aria-label="First Name" autoComplete="disable" {...register('givenName')} />
              </StyledField>
              <Spacer />
              <StyledField errorMessage={errors.familyName ?.message} error={errors.familyName} label="Last Name">
                <NameInput aria-label="Last Name" autoComplete="disable" {...register('familyName')} />
              </StyledField>
            </NameContainer>
            <StyledField errorMessage={errors.email ?.message} error={errors.email} label="Email Address">
              <TextInput aria-label="Email" type="email" readOnly={!!queryParams.email} styleDisabled={!!queryParams.email} autoComplete="disable" {...register('email')}/>
            </StyledField>
            <StyledField label="Password">
              <PasswordInput aria-label="Password" type="password" {...register('password')} autoComplete="new-password" onChange={debounce(passwordChangeHandler)} />
            </StyledField>
            {passwordStrength.description && <PasswordStrength color={passwordStrength.color} value={passwordStrength.value} copy={passwordStrength.description as string} />}
            <StyledField errorMessage={errors.agreedToTOSandPP ?.message} error={errors.agreedToTOSandPP}>
              <Controller name="agreedToTOSandPP" control={control}
                render={({ field: { onChange, ref } }) => {

                  return <Checkbox aria-label="Agree to Terms of Service and Privacy Policy" onChange={onChange} ref={ref} />

                }} />
              <CheckboxCopy>I agree to the <StyledA href="/legal/#terms-of-use" target="_blank">Terms of Service</StyledA> and <StyledA href="/about/privacy/" target="_blank">Privacy Policy</StyledA>.</CheckboxCopy>
            </StyledField>
            <StyledField errorMessage={errors.marketingOptInStatus ?.message} error={errors.marketingOptInStatus}>
              <Controller name="marketingOptInStatus" control={control}
                render={({ field: { onChange, ref } }) => {
                  return <Checkbox onChange={onChange} ref={ref} />
                }} />
              <CheckboxCopy>I agree to receive marketing communications from BitPay</CheckboxCopy>
            </StyledField>
            <CaptchaContainer>
              {!captchaDisabled &&
                <ReCAPTCHA
                  sitekey={captchaKey}
                  size="invisible"
                  badge="inline"
                  ref={recaptchaRef}
                />}
            </CaptchaContainer>
            <SubmitButton type="submit" role="button" aria-label="submit" disabled={passwordStrength.value < 75}>Submit</SubmitButton>
            {!captchaDisabled &&
              <CaptchaLegal>
                This site is protected by reCAPTCHA and the Google
              <StyledA
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noreferrer noopener"
                > Privacy Policy </StyledA>
                and
              <StyledA
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noreferrer noopener"
                > Terms of Service </StyledA>
                apply.
            </CaptchaLegal>
            }
          </Form>
        </AnimatePresence>
    )}
    </>
  );
});

export default PersonalInfoForm;
